import apiClient from '../../../lib/api-client';
import { convertFiltersToServerFormat, type ServerFilter } from '../../../lib/utils';
import type { 
  User, 
  UsersResponse,
  UserStats,
  UserFilters, 
  UserSubmitRequest,
  UserUpdateRequest,
  GetUsersParams,
  UserImportResult,
  BulkUpdateRequest,
  BulkUpdateResult
} from '../types';


class UserService {
  private readonly baseUrl = '/user';

  private stripEmptyOptionalFields<T extends object>(
    data: T,
    fields: (keyof T)[]
  ): T {
    const payload = { ...data };
    fields.forEach((field) => {
      const value = payload[field];
      if (value === '' || value === null || value === undefined) {
        delete payload[field];
      }
    });
    return payload;
  }

  private convertFiltersToServerFormat(filters: UserFilters): ServerFilter[] {
    const fieldMappings = {
      role: { field: 'role', operator: 'eq' as const },
      type: { field: 'type', operator: 'eq' as const },
      status: { field: 'status', operator: 'eq' as const },
      search: { field: 'search', operator: 'like' as const }, // Changed from 'name' to 'search' to trigger multi-field search
      schoolId: { field: 'schoolId', operator: 'eq' as const },
      grade: { field: 'grade', operator: 'eq' as const },
      class: { field: 'class', operator: 'eq' as const },
      unit: { field: 'unit', operator: 'eq' as const }
    };

    return convertFiltersToServerFormat(filters, fieldMappings);
  }

  async getUsers(params: GetUsersParams = {}): Promise<UsersResponse> {
    const { page = 1, per_page = 15, ...filters } = params;
    const serverFilters = this.convertFiltersToServerFormat(filters);
    
    // Send pagination parameters separately to ensure they're not lost
    const requestData = {
      filters: serverFilters,
      pagination: {
        page: page,
        per_page: per_page
      }
    };
    
    const response = await apiClient.post(`${this.baseUrl}/search`, requestData);
    
    // Transform the server response to match our expected format
    const serverData = response.data.data;
    return {
      data: serverData.data || [],
      pagination: {
        current_page: serverData.current_page || 1,
        per_page: serverData.per_page || 15,
        total: serverData.total || 0,
        last_page: serverData.last_page || 1
      }
    };
  }

  async getStats(): Promise<UserStats> {
    const response = await apiClient.get(`${this.baseUrl}/stats`);
    return response.data.data;
  }

  async getUserById(id: string): Promise<User> {
    const response = await apiClient.get(`${this.baseUrl}/${id}`);
    return response.data.data;
  }

  async createUser(data: UserSubmitRequest): Promise<User> {
    const payload = this.stripEmptyOptionalFields(data, ['email', 'password', 'phone']);
    const response = await apiClient.post(this.baseUrl, payload);
    return response.data.data;
  }

  async updateUser(id: string, data: UserUpdateRequest): Promise<User> {
    const payload = this.stripEmptyOptionalFields(data, ['email', 'password', 'phone']);
    const response = await apiClient.put(`${this.baseUrl}/${id}`, payload);
    return response.data.data;
  }

  async updateUserRoles(id: string, roles: string[]): Promise<User> {
    const response = await apiClient.put(`${this.baseUrl}/${id}`, { roles });
    return response.data?.data;
  }

  async updateUserPassword(id: string, password: string): Promise<void> {
    await apiClient.put(`${this.baseUrl}/${id}`, { password });
  }

  async deleteUser(id: string): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}`);
  }

  async bulkUpdateUsers(data: BulkUpdateRequest): Promise<BulkUpdateResult> {
    const response = await apiClient.post(`${this.baseUrl}/bulk-update`, data);
    return response.data.data;
  }

  async downloadTemplate(type: 'student' | 'teacher'): Promise<Blob> {
    const response = await apiClient.get(`${this.baseUrl}/template/${type}`, {
      responseType: 'blob'
    });
    return response.data;
  }

  async previewImport(file: File, schoolId?: string, type: 'student' | 'teacher' = 'student'): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    if (schoolId) formData.append('schoolId', schoolId);
    formData.append('type', type);

    const response = await apiClient.post(`${this.baseUrl}/preview-import`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    
    return response.data;
  }

  async importUsers(file: File, schoolId?: string, schoolName?: string, type: 'student' | 'teacher' = 'student'): Promise<UserImportResult> {
    const formData = new FormData();
    formData.append('file', file);
    if (schoolId) formData.append('schoolId', schoolId);
    if (schoolName) formData.append('schoolName', schoolName);
    formData.append('type', type);

    const response = await apiClient.post(`${this.baseUrl}/import`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    
    return response.data;
  }

  async toggleUserStatus(id: string, status: string): Promise<void> {
    await apiClient.put(`${this.baseUrl}/${id}`, { status });
  }

  async resetPassword(id: string): Promise<{ plain_password: string; message: string }> {
    const response = await apiClient.post(`${this.baseUrl}/${id}/reset-password`);
    return response.data.data;
  }

  async getUsersByRole(role: string, params: GetUsersParams = {}): Promise<UsersResponse> {
    return this.getUsers({ ...params, role });
  }

  async getUsersBySchool(schoolId: string, params: GetUsersParams = {}): Promise<UsersResponse> {
    return this.getUsers({ ...params, schoolId });
  }

  async getStudents(params: GetUsersParams = {}): Promise<UsersResponse> {
    return this.getUsers({ ...params, role: 'student' });
  }

  async getTeachers(params: GetUsersParams = {}): Promise<UsersResponse> {
    return this.getUsers({ ...params, role: 'teacher' });
  }
}

export default new UserService();
